import React, { useState, useEffect } from 'react';
import * as Plot from "@observablehq/plot";
import PlotFigure from '../PlotFigure';
import DeviceDropdown from '../components/dashboard/DeviceDropdown';
import IntervalButtons from '../components/dashboard/IntervalButtons';
import ErrorMessage from '../components/common/ErrorMessage';
import { useChUtilData } from '../hooks/useChUtilData';
import { WifiIcon } from '../components/common/Icons';
import { apiClient } from '../utils/api';
import { useAuth } from '../auth/AuthContext';

export default function ChUtilPage({ 
    selectedDevice, 
    onDeviceChange, 
    selectedInterval, 
    customRange, 
    onIntervalChange, 
    onRangeChange 
}) {
    const [devices, setDevices] = useState([]);
    const { user } = useAuth();
    const userId = user?.id;

    // Load available devices for the dropdown
    useEffect(() => {
        apiClient.getDevices().then(list => {
            const filtered = userId ? list.filter((device) => device.user_id === userId) : list;
            setDevices(filtered);
            if (filtered.length > 0 && !selectedDevice) {
                onDeviceChange(filtered[0].id);
            }
        }).catch(err => console.error("Failed to load devices", err));
    }, [userId, selectedDevice, onDeviceChange]);

    const { data, loading, error, refresh } = useChUtilData(
        selectedDevice,
        selectedInterval,
        customRange
    );

    return (
        <div className="dashboard-content dashboard-content--full-width animate-fade-in fade-in">
            <header style={{ marginBottom: '20px' }}>
                <h2><WifiIcon size="28px" /> Channel Utilization</h2>
                <p className="subtitle">High-fidelity airtime usage metrics.</p>
            </header>

            <section className="data-controls">
                <div className="data-controls__layout">
                    <div className="data-controls__device">
                        <div className="data-controls__title">Data analysis controls</div>
                        <DeviceDropdown 
                            devices={devices}
                            selectedDevice={selectedDevice} 
                            onDeviceChange={onDeviceChange} 
                            disabled={loading}
                        />
                    </div>
                    <span className="data-controls__divider" aria-hidden="true" />
                    <div className="data-controls__interval">
                        <div className="data-controls__interval-label">Data interval</div>
                        <div className="interval-buttons-with-refresh" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <IntervalButtons
                                selectedInterval={selectedInterval}
                                onIntervalChange={onIntervalChange}
                                customRange={customRange}
                                onCustomRangeChange={onRangeChange}
                                disabled={loading}
                            />
                            <button
                                type="button"
                                onClick={refresh}
                                className={`btn-icon ${loading ? 'is-spinning' : ''}`}
                                title="Refresh data"
                            >
                                ↻
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {error && (
                <div className="dashboard-error">
                    <ErrorMessage message={error} />
                </div>
            )}

            {loading && !data && (
                <div className="dashboard-loading">
                    <div className="spinner"></div>
                    <p>Loading channel utilization data...</p>
                </div>
            )}

            {!loading && !error && data && (
                <ChUtilPlots data={data.records || []} />
            )}
            
            {!loading && !error && data && (!data.records || data.records.length === 0) && (
                <div className="no-data-message">
                    <p>No channel utilization data found for this interval.</p>
                </div>
            )}
        </div>
    );
}

function ChUtilPlots({ data }) {
    if (!data || data.length === 0) return null;

    // Transform strings into Date objects
    const plotData = data.map(d => ({
        ...d,
        t: new Date(d.ts),
        calc_util: typeof d.utilization_percent === 'number' ? d.utilization_percent : parseFloat(d.utilization_percent || 0),
        bss_load: typeof d.ap_bss_load_percent === 'number' ? d.ap_bss_load_percent : parseFloat(d.ap_bss_load_percent || 0),
    }));

    // Find max value to align y-axes roughly if desired, or let Plot auto-scale
    const calcPlotOptions = {
        y: {
            grid: true,
            label: "Calculated Util (%)",
            domain: [0, 100] // Cap at 100%
        },
        x: {
            type: 'time',
            grid: true,
        },
        color: {
            legend: true
        },
        marks: [
            Plot.ruleY([0]),
            Plot.line(plotData, {
                x: 't',
                y: 'calc_util',
                stroke: '#3ba4ff'
            }),
            Plot.tip(
                plotData,
                Plot.pointerX({
                    x: 't',
                    y: 'calc_util',
                    title: (d) => `Calculated Utilization: ${d.calc_util.toFixed(2)}%\nTime: ${d.t.toLocaleString()}`,
                    pointer: 'mouse',
                    className: 'plot-tip neon-plot-tip',
                    textPadding: 12,
                    pointerSize: 14,
                    lineHeight: 1.6,
                    fontSize: 12,
                    fontWeight: '600',
                })
            ),
        ],
    };

    const bssPlotOptions = {
        y: {
            grid: true,
            label: "AP BSS Load (%)",
            domain: [0, 100] // Cap at 100%
        },
        x: {
            type: 'time',
            grid: true,
        },
         color: {
            legend: true
        },
        marks: [
            Plot.ruleY([0]),
            Plot.line(plotData, {
                x: 't',
                y: 'bss_load',
                stroke: '#ff8c3a' // Orange for contrast
            }),
            Plot.tip(
                plotData,
                Plot.pointerX({
                    x: 't',
                    y: 'bss_load',
                    title: (d) => `AP BSS Load: ${d.bss_load.toFixed(2)}%\nTime: ${d.t.toLocaleString()}`,
                    pointer: 'mouse',
                    className: 'plot-tip neon-plot-tip',
                    textPadding: 12,
                    pointerSize: 14,
                    lineHeight: 1.6,
                    fontSize: 12,
                    fontWeight: '600',
                })
            ),
        ],
    };

    return (
        <div className="plots-container">
            <div className="plot-row">
                 <div className="plot-column">
                     <div className="plot-section">
                        <header className="plot-section__header plot-section__header--compact">
                            <h3><WifiIcon size="24px" /> Calculated Utilization (Sniffer)</h3>
                        </header>
                        <PlotFigure options={calcPlotOptions} />
                    </div>
                </div>
            </div>
            <div className="plot-row">
                 <div className="plot-column">
                     <div className="plot-section">
                        <header className="plot-section__header plot-section__header--compact">
                            <h3><WifiIcon size="24px" /> AP BSS Load Element</h3>
                        </header>
                         <PlotFigure options={bssPlotOptions} />
                     </div>
                </div>
            </div>
        </div>
    );
}
